import { useEffect, useState } from 'react';
import { fetchLiveStreamSettings, updateLiveStreamSettings } from '@/lib/api';
import type { LiveStreamSettings } from '@/lib/types';

export default function LiveStreamAdminPage() {
  const [form, setForm] = useState<LiveStreamSettings>({
    id: 1,
    is_live: false,
    title: '',
    embed_url: '',
    description: '',
    next_service_time: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchLiveStreamSettings()
      .then((s) => setForm(s))
      .catch((err) => setError(err instanceof Error ? err.message : 'Failed to load'))
      .finally(() => setLoading(false));
  }, []);

  const set = (key: keyof LiveStreamSettings, value: string | boolean) =>
    setForm((p) => ({ ...p, [key]: value }));

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);
    setError(null);
    try {
      await updateLiveStreamSettings({
        is_live: form.is_live,
        title: form.title,
        embed_url: form.embed_url,
        description: form.description,
        next_service_time: form.next_service_time,
      });
      setMessage('Live stream settings saved successfully.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="py-16 text-center text-foreground-500">
        <i className="ri-loader-4-line animate-spin text-2xl inline-block" aria-hidden="true" />
      </div>
    );
  }

  const inputClass =
    'w-full px-3.5 py-2.5 rounded-md text-sm border border-background-300 bg-white outline-none focus:ring-2 focus:ring-primary-300 text-foreground-900';
  const labelClass = 'block text-xs font-label font-medium text-foreground-700 mb-1.5';

  return (
    <div className="max-w-3xl">
      <div className="mb-6">
        <h2 className="font-heading font-semibold text-xl text-foreground-900">Live Stream</h2>
        <p className="text-sm text-foreground-500 mt-1">
          Go live on the public site. When live is on, the &ldquo;Watch Live&rdquo; button appears and prayer requests open.
        </p>
      </div>

      {message && (
        <div className="mb-4 px-4 py-3 rounded-md bg-accent-100 text-accent-900 text-sm border border-accent-300">
          {message}
        </div>
      )}
      {error && (
        <div className="mb-4 px-4 py-3 rounded-md bg-red-50 text-red-600 text-sm border border-red-200">
          {error}
        </div>
      )}

      <div className="space-y-6">
        {/* Live toggle */}
        <section className="bg-white rounded-lg border border-background-200 p-6">
          <div className="flex items-center justify-between gap-4">
            <div>
              <h3 className="font-heading font-semibold text-base text-foreground-900">Stream Status</h3>
              <p className="text-sm text-foreground-500 mt-1">
                {form.is_live ? 'Your stream is currently LIVE to the public.' : 'Your stream is currently offline.'}
              </p>
            </div>
            <button
              type="button"
              onClick={() => set('is_live', !form.is_live)}
              className={`relative w-16 h-8 rounded-full transition-colors cursor-pointer shrink-0 ${
                form.is_live ? 'bg-red-500' : 'bg-background-300'
              }`}
              aria-label={form.is_live ? 'Go offline' : 'Go live'}
              aria-pressed={form.is_live}
            >
              <span
                className={`absolute top-1 w-6 h-6 rounded-full bg-white shadow transition-all ${
                  form.is_live ? 'left-9' : 'left-1'
                }`}
              />
            </button>
          </div>
        </section>

        {/* Details */}
        <section className="bg-white rounded-lg border border-background-200 p-6 space-y-4">
          <h3 className="flex items-center gap-2 font-heading font-semibold text-base text-foreground-900">
            <i className="ri-vidicon-line text-primary-600" aria-hidden="true" /> Stream Details
          </h3>
          <div>
            <label className={labelClass}>Stream Title</label>
            <input type="text" value={form.title} onChange={(e) => set('title', e.target.value)} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Embed URL (iframe src)</label>
            <input type="url" value={form.embed_url} onChange={(e) => set('embed_url', e.target.value)} className={inputClass} placeholder="https://www.youtube.com/embed/..." />
            <p className="mt-1 text-xs text-foreground-400">
              Paste the embed URL from YouTube, Facebook Live, or your streaming platform (the &ldquo;src&rdquo; of the embed iframe).
            </p>
          </div>
          <div>
            <label className={labelClass}>Description</label>
            <textarea value={form.description} onChange={(e) => set('description', e.target.value)} className={`${inputClass} resize-y`} rows={3} />
          </div>
          <div>
            <label className={labelClass}>Next Service Time</label>
            <input type="text" value={form.next_service_time} onChange={(e) => set('next_service_time', e.target.value)} className={inputClass} placeholder="Sunday 10:00 AM" />
          </div>
        </section>

        <div className="flex justify-end">
          <button type="button" onClick={handleSave} disabled={saving} className="btn-primary whitespace-nowrap">
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}